import { announce } from '@theme/component';

(() => {
  const openCookiePreferences = () => {
    if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
      window.privacyBanner.showPreferences().catch((error) => {
        console.error('Failed to open cookie preferences:', error);
      });
    } else if (
      window.Shopify
      && window.Shopify.customerPrivacy
      && typeof window.Shopify.customerPrivacy.showPreferencesPopup === 'function'
    ) {
      window.Shopify.customerPrivacy.showPreferencesPopup();
    } else {
      console.warn('Cookie consent system not available. Ensure privacy settings are configured in Shopify admin.');
    }
  };

  const extractErrorMessage = (errors) => {
    if (!errors) return '';
    if (typeof errors === 'string') return errors;
    if (Array.isArray(errors) && errors.length > 0) {
      const first = errors[0];
      if (typeof first === 'string') return first;
      return (first && first.message) || '';
    }
    return errors.message || errors.detail || '';
  };

  if (!customElements.get('dieux-footer')) {
    customElements.define(
      'dieux-footer',
      class DieuxFooter extends HTMLElement {
        connectedCallback() {
          this.abortController = new AbortController();
          const { signal } = this.abortController;

          this.initNewsletter(signal);

          this.querySelectorAll('[data-cookie-preferences-trigger]').forEach((trigger) => {
            trigger.addEventListener('click', openCookiePreferences, { signal });
          });
        }

        disconnectedCallback() {
          if (this.abortController) this.abortController.abort();
        }

        initNewsletter(signal) {
          const form = this.querySelector('[data-dieux-footer-form]');
          if (!form) return;

          const emailInput = form.querySelector('[data-dieux-footer-email]');
          const errorRegion = form.querySelector('[data-dieux-footer-form-error]');
          const statusRegion = form.querySelector('[data-dieux-footer-form-status]');
          const successRegion = this.querySelector('[data-dieux-footer-success]');
          if (!emailInput || !errorRegion || !statusRegion || !successRegion) return;

          const showError = (message, { moveFocus = false } = {}) => {
            errorRegion.textContent = message;
            emailInput.setAttribute('aria-invalid', 'true');
            emailInput.setAttribute('aria-describedby', errorRegion.id);
            if (moveFocus && document.activeElement !== emailInput) {
              statusRegion.textContent = '';
              emailInput.focus({ preventScroll: true });
              return;
            }
            announce(statusRegion, message);
          };

          const clearError = () => {
            if (errorRegion.textContent === '') return;
            errorRegion.textContent = '';
            statusRegion.textContent = '';
            emailInput.removeAttribute('aria-invalid');
            emailInput.removeAttribute('aria-describedby');
          };

          emailInput.addEventListener('input', clearError, { signal });

          form.addEventListener(
            'submit',
            (event) => {
              event.preventDefault();

              const value = emailInput.value.trim();
              if (value === '' || !emailInput.checkValidity()) {
                showError(
                  value === '' ? form.dataset.emptyMessage || form.dataset.invalidMessage : form.dataset.invalidMessage,
                  { moveFocus: true }
                );
                return;
              }
              if (this.newsletterPending) return;
              this.newsletterPending = true;
              form.setAttribute('aria-busy', 'true');

              const params = new URLSearchParams();
              const fields = ['$source'];
              params.set('g', form.elements.g.value);
              params.set('email', value);
              params.set('$source', form.dataset.signupSource || document.title);

              const pageParams = new URLSearchParams(window.location.search);
              const utmSource = pageParams.get('utm_source');
              const utmCampaign = pageParams.get('utm_campaign');
              if (utmSource) {
                fields.push('$utm_source');
                params.set('$utm_source', utmSource);
              }
              if (utmCampaign) {
                fields.push('$utm_campaign');
                params.set('$utm_campaign', utmCampaign);
              }
              params.set('$fields', fields.join(','));

              fetch(form.dataset.ajaxSubmit, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: params.toString(),
                signal,
              })
                .then((response) => {
                  if (!response.ok) throw new Error('Network response was not ok');
                  return response.json();
                })
                .then((data) => {
                  if (data && (data.success || data.data)) {
                    clearError();
                    const focusWasInForm = form.contains(document.activeElement);
                    form.hidden = true;
                    successRegion.textContent = form.dataset.successMessage;
                    if (focusWasInForm) window.DieuxAnchorScroll?.focusLanding?.(successRegion);
                    return;
                  }
                  showError(extractErrorMessage(data && data.errors) || form.dataset.errorMessage);
                })
                .catch((error) => {
                  if (error && error.name === 'AbortError') return;
                  showError(form.dataset.errorMessage);
                })
                .finally(() => {
                  this.newsletterPending = false;
                  form.removeAttribute('aria-busy');
                });
            },
            { signal }
          );
        }
      }
    );
  }
})();
